/**
 * Mololink Routes - Logistics Services Marketplace
 * 
 * @description Frontend route definitions for the Mololink subdomain (mololink.molochain.com)
 * @backend server/routes/mololink.ts - API endpoints at /api/mololink/*
 * @registrar server/registrars/ecosystem.registrar.ts
 * 
 * AND without prefix (for mololink.molochain.com/*) using subdomain: 'mololink'.
 */
import { lazy } from 'react';
import { RouteRegistry } from './RouteRegistry';

const MololinkMain = lazy(() => import('@/modules/mololink/MololinkMain'));
const NotFound = lazy(() => import('@/pages/general/not-found'));
const MololinkHome = lazy(() => import('@/modules/mololink/pages/home'));
const MololinkMarketplace = lazy(() => import('@/modules/mololink/pages/marketplace'));
const MololinkCompanies = lazy(() => import('@/modules/mololink/pages/companies'));
const MololinkCompanyProfile = lazy(() => import('@/modules/mololink/pages/company-profile'));
const MololinkMessaging = lazy(() => import('@/modules/mololink/pages/messaging'));
const MololinkExplorer = lazy(() => import('@/modules/mololink/pages/explorer'));
const MololinkSearch = lazy(() => import('@/modules/mololink/pages/search'));
const MololinkNotifications = lazy(() => import('@/modules/mololink/pages/notifications'));
const MololinkSolutions = lazy(() => import('@/modules/mololink/pages/solutions'));
const MololinkResources = lazy(() => import('@/modules/mololink/pages/resources'));
const MololinkPricing = lazy(() => import('@/modules/mololink/pages/pricing'));

const MololinkRequests = lazy(() => import('@/modules/mololink/pages/requests'));
const MololinkRequestNew = lazy(() => import('@/modules/mololink/pages/request-new'));
const MololinkRequestDetail = lazy(() => import('@/modules/mololink/pages/request-detail'));
const MololinkAuctions = lazy(() => import('@/modules/mololink/pages/auctions'));
const MololinkAuctionDetail = lazy(() => import('@/modules/mololink/pages/auction-detail'));
const MololinkAwardConfirmation = lazy(() => import('@/modules/mololink/pages/award-confirmation'));
const MololinkProviders = lazy(() => import('@/modules/mololink/pages/providers'));
const MololinkProviderProfile = lazy(() => import('@/modules/mololink/pages/provider-profile'));
const MololinkHandoffSuccess = lazy(() => import('@/modules/mololink/pages/handoff-success'));
const OtmsRedirect = lazy(() => import('@/components/mololink/OtmsRedirect'));

RouteRegistry.registerCategory({
  name: 'mololink',
  order: 7,
  routes: [
    // === Landing (mololink.molochain.com/) ===
    { path: '/', component: MololinkMain, exact: true, layout: 'mololink', subdomain: 'mololink' },

    // === Subdomain routes (mololink.molochain.com/X) ===
    { path: '/home', component: MololinkHome, requireAuth: true, layout: 'mololink', subdomain: 'mololink' },
    { path: '/marketplace', component: MololinkMarketplace, layout: 'mololink', subdomain: 'mololink' },
    { path: '/requests', component: MololinkRequests, requireAuth: true, layout: 'mololink', subdomain: 'mololink' },
    { path: '/requests/new', component: MololinkRequestNew, requireAuth: true, layout: 'mololink', subdomain: 'mololink' },
    { path: '/requests/:id', component: MololinkRequestDetail, requireAuth: true, layout: 'mololink', subdomain: 'mololink' },
    { path: '/auctions', component: MololinkAuctions, requireAuth: true, layout: 'mololink', subdomain: 'mololink' },
    { path: '/auctions/:id', component: MololinkAuctionDetail, requireAuth: true, layout: 'mololink', subdomain: 'mololink' },
    { path: '/award/:id', component: MololinkAwardConfirmation, requireAuth: true, layout: 'mololink', subdomain: 'mololink' },
    { path: '/providers', component: MololinkProviders, layout: 'mololink', subdomain: 'mololink' },
    { path: '/provider/:id', component: MololinkProviderProfile, layout: 'mololink', subdomain: 'mololink' },
    { path: '/handoff-success', component: MololinkHandoffSuccess, requireAuth: true, layout: 'mololink', subdomain: 'mololink' },
    { path: '/companies', component: MololinkCompanies, layout: 'mololink', subdomain: 'mololink' },
    { path: '/company/:id', component: MololinkCompanyProfile, layout: 'mololink', subdomain: 'mololink' },
    { path: '/messaging', component: MololinkMessaging, requireAuth: true, layout: 'mololink', subdomain: 'mololink' },
    { path: '/explorer', component: MololinkExplorer, requireAuth: true, layout: 'mololink', subdomain: 'mololink' },
    { path: '/search', component: MololinkSearch, layout: 'mololink', subdomain: 'mololink' },
    { path: '/notifications', component: MololinkNotifications, requireAuth: true, layout: 'mololink', subdomain: 'mololink' },
    { path: '/solutions', component: MololinkSolutions, layout: 'mololink', subdomain: 'mololink' },
    { path: '/resources', component: MololinkResources, layout: 'mololink', subdomain: 'mololink' },
    { path: '/pricing', component: MololinkPricing, layout: 'mololink', subdomain: 'mololink' },
    
    // OPT redirects on mololink subdomain
    { path: '/bookings', component: OtmsRedirect, layout: 'mololink', subdomain: 'mololink' },
    { path: '/shipments', component: OtmsRedirect, layout: 'mololink', subdomain: 'mololink' },
    { path: '/quotes', component: OtmsRedirect, layout: 'mololink', subdomain: 'mololink' },
    { path: '/tracking', component: OtmsRedirect, layout: 'mololink', subdomain: 'mololink' },
    { path: '/documents', component: OtmsRedirect, layout: 'mololink', subdomain: 'mololink' },
    { path: '/invoices', component: OtmsRedirect, layout: 'mololink', subdomain: 'mololink' },


    // Catch-all for mololink subdomain
    { path: '*', component: NotFound, layout: 'mololink', subdomain: 'mololink' },
  ]
});

export const mololinkRoutes = RouteRegistry.getRoutesByCategory('mololink');
